/**
 * Stage 7, continued: back stock. A **reserve** is a rule -- "keep at least
 * four laptops tagged 17-inch and 256 GB for Client 1" -- and the units that
 * make it true are **holds**: stock on the shelf, pulled into the reserve and
 * earmarked for that client until it is deployed to them, released, or taken
 * for somebody else.
 *
 * A reserve with no client is a general-pool rule ("keep five unreserved
 * 17-inch laptops"). Nothing can be held for it; its count is whatever matching
 * stock is on the shelf and not held for anyone.
 *
 * **Tags are the rule, matched by name, all of them.** Stored as names rather
 * than tag ids, because a tag nothing wears is pruned from the tag table on the
 * next write, and a rule that silently pointed at nothing would be worse than
 * no rule. The tags a rule can use are **reserve tags**: a flagged list of
 * their own, made with the reserves and picked by clicking -- on the reserve
 * form and on each receiving line -- rather than typed, so "256GB" and
 * "256 GB" cannot quietly become two rules' worth of stock.
 *
 * **Paid in advance** is per reserve, because some clients pay for held stock
 * and some do not. A prepaid hold is a billing line of its own on the month-end
 * export; deploying it to its own client later bills nothing more. When a unit
 * the client already paid for leaves the reserve some other way -- taken for a
 * different client, released, retired -- the reserve keeps a **credit**: units
 * paid for and not held, which the next hold for that reserve uses up before
 * billing again. See docs/notes/decisions.md.
 */

/** Why an alert is quiet. "Ordered" is the common one: stock is on its way, so stop telling me. */
export type ReserveQuietReason = "ordered" | "snoozed";

export const RESERVE_QUIET_LABELS: Record<ReserveQuietReason, string> = {
  ordered: "Ordered",
  snoozed: "Snoozed",
};

/**
 * `short` is below the minimum and nobody has said why; `quiet` is below the
 * minimum with a date somebody gave (ordered, or snoozed) that has not passed.
 */
export type ReserveStatus = "ok" | "short" | "quiet";

export interface Reserve {
  id: string;
  /** Null for a general-pool rule. */
  clientId: string | null;
  clientName: string;
  /** Every one must be on an item for it to count. */
  tags: string[];
  /** Below this, the reserve is short and alerts. */
  minimum: number;
  /** What "fill" tops it up to, and what an order should bring it back to. Never below the minimum. */
  target: number;
  prepaid: boolean;
  /** Prepaid units the client paid for that are not held any more. Used up by the next holds. */
  credit: number;
  note: string;
  /** `YYYY-MM-DD`. Quiet up to and including this day. */
  quietUntil: string | null;
  quietReason: ReserveQuietReason | null;
  quietNote: string;
  createdAt: number;
  /** Held units for a client reserve; unheld matching units on the shelf for a pool rule. */
  count: number;
  /** Matching units on the shelf that nobody holds: what "fill" can take. */
  free: number;
  status: ReserveStatus;
  /** When it last went below the minimum and stayed there; null while it is not short. */
  shortSince: number | null;
}

/** Units of one item held for one reserve, as the reserve's own card lists them. */
export interface ReserveHold {
  id: string;
  reserveId: string;
  itemId: string;
  title: string;
  serial: string;
  tracking: "serialized" | "bulk";
  /** Still held: what came in, less anything deployed, released or taken since. */
  open: number;
  /** Only on a prepaid hold that bills: the price each. */
  unitPriceCents: number | null;
  /** How it is paid: not at all (earmarked), billed on the export, or covered by an earlier payment. */
  billing: HoldBilling;
  /** When its billing line was marked billed; null while waiting, or when it does not bill. */
  billedAt: number | null;
  heldOn: string;
  actor: string;
}

export type HoldBilling = "none" | "bill" | "credit";

/** A hold as the item screen shows it: which client it is for, and by what rule. */
export interface ItemHold extends ReserveHold {
  clientId: string;
  clientName: string;
  tags: string[];
}

/** Stock that matches a reserve's tags and is free to be held for it. */
export interface ReserveCandidate {
  itemId: string;
  title: string;
  serial: string;
  tracking: "serialized" | "bulk";
  free: number;
  unitCostCents: number | null;
}

export interface ReserveDetail {
  reserve: Reserve;
  holds: ReserveHold[];
  candidates: ReserveCandidate[];
}

/** What a shelf row carries about who its units are held for. */
export interface RowHold {
  reserveId: string;
  clientId: string;
  clientName: string;
  quantity: number;
}

/**
 * A **reserve tag**: a tag the stockroom has flagged as one reserves match on.
 * Its own short list, kept even while no item wears it, so the reserve form
 * and the receiving screen can offer it to click rather than to type -- which
 * is what keeps a rule from matching on "256GB" when the stock says "256 GB".
 * Items wear it as an ordinary tag, so search finds them by it as by any other.
 */
export interface ReserveTag {
  name: string;
  /** Items wearing it now. */
  itemCount: number;
  /** Reserves whose rule uses it. */
  reserveCount: number;
}

export const MAX_RESERVE_TAG_LIST = 100;

/* ------------------------------------------------------------- writes -- */

export interface ReserveInput {
  clientId: string | null;
  tags: string[];
  minimum: number;
  target: number;
  prepaid: boolean;
  note: string;
  /** Only on an edit, and only for a prepaid reserve: corrects what the client is owed. */
  credit?: number;
}

export interface HoldLineInput {
  itemId: string;
  quantity: number;
  /** A prepaid reserve bills each unit; absent, the price is cost plus the standard markup. */
  unitPriceCents?: number | null;
}

export interface HoldInput {
  heldOn: string;
  lines: HoldLineInput[];
}

export interface ReleaseInput {
  quantity: number;
  releasedOn: string;
}

export interface QuietInput {
  /** Null clears it. */
  until: string | null;
  reason: ReserveQuietReason;
  note: string;
}

/* --------------------------------------------------------------- alerts -- */

/**
 * How often somebody is told. Never "every time": stock is shuffled, and a
 * reserve that dips for a minute while a laptop is swapped for another is not
 * news. Hourly mails only when something new went short; daily and weekly are
 * a summary of whatever is short at the time, if anything is.
 */
export type AlertFrequency = "off" | "hourly" | "daily" | "weekly";

export const ALERT_FREQUENCIES: readonly AlertFrequency[] = ["off", "hourly", "daily", "weekly"];

export const ALERT_FREQUENCY_LABELS: Record<AlertFrequency, string> = {
  off: "Off",
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
};

export interface AlertPrefs {
  frequency: AlertFrequency;
  /** An IANA zone ("America/Chicago"), so "daily" arrives in the morning where the person is. */
  timeZone: string;
}

/**
 * How long a reserve has to stay short before any alert mentions it. The
 * shuffle case: a laptop taken for one client and replaced from the next
 * delivery an hour later should not have mailed anybody in between.
 */
export const ALERT_GRACE_SECONDS = 15 * 60;

/** The local hour a daily or weekly summary goes out, and the weekday (1 = Monday) a weekly one does. */
export const DIGEST_HOUR = 7;
export const DIGEST_WEEKDAY = 1;

/* ------------------------------------------------------------- limits -- */

export const MAX_RESERVES = 200;
export const MAX_RESERVE_TAGS = 10;
export const MAX_RESERVE_UNITS = 99_999;

/* ------------------------------------------------------------ helpers -- */

/** A reserve's rule the way a person says it: "17-inch and 256 GB", "a, b and c". */
export const reserveTagLine = (tags: readonly string[]): string =>
  tags.length <= 1 ? (tags[0] ?? "") : `${tags.slice(0, -1).join(", ")} and ${tags[tags.length - 1]}`;

/** Who a reserve is for, as a heading. */
export const reserveOwner = (reserve: Pick<Reserve, "clientId" | "clientName">): string =>
  reserve.clientId ? reserve.clientName : "General pool";

/** Units to order to bring a short reserve back to its target. */
export const reserveShortfall = (reserve: Pick<Reserve, "count" | "target">): number =>
  Math.max(reserve.target - reserve.count, 0);

/**
 * Whether a digest is due for somebody right now: hourly always is (and mails
 * only when there is something new), daily and weekly at the digest hour in
 * the person's own zone, once. `lastDigestAt` is when one was last considered
 * for them, sent or not.
 */
export function digestDue(
  frequency: AlertFrequency,
  timeZone: string,
  lastDigestAt: number,
  now: number,
): boolean {
  if (frequency === "off") return false;
  if (frequency === "hourly") return now - lastDigestAt >= 50 * 60;
  const local = localClock(timeZone, now);
  if (local.hour !== DIGEST_HOUR) return false;
  if (frequency === "daily") return now - lastDigestAt >= 20 * 3600;
  return local.weekday === DIGEST_WEEKDAY && now - lastDigestAt >= 6 * 86400;
}

/** The hour and weekday (1 = Monday ... 7 = Sunday) at `now` in a zone; UTC when the zone is not one. */
export function localClock(timeZone: string, now: number): { hour: number; weekday: number } {
  const date = new Date(now * 1000);
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timeZone || "UTC",
      hour: "numeric",
      hourCycle: "h23",
      weekday: "short",
    }).formatToParts(date);
    const hour = Number(parts.find((part) => part.type === "hour")?.value ?? date.getUTCHours());
    const day = parts.find((part) => part.type === "weekday")?.value ?? "";
    const weekday = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(day) + 1;
    return { hour: hour % 24, weekday: weekday || ((date.getUTCDay() + 6) % 7) + 1 };
  } catch {
    return { hour: date.getUTCHours(), weekday: ((date.getUTCDay() + 6) % 7) + 1 };
  }
}

/** A zone the runtime knows, or UTC. Checked on the way in so the digest never has to guess. */
export function validTimeZone(value: unknown): string {
  if (typeof value !== "string" || !value || value.length > 64) return "UTC";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: value });
    return value;
  } catch {
    return "UTC";
  }
}
